import React, { useRef, useState } from 'react'
import {
  useFlatfile,
  Space,
  useListener,
  Workbook,
  Document,
} from '@flatfile/react'
import {
  RecordNames,
  RecordTypes,
  getFlatfileSchema,
  UploadInstructions,
} from 'flatfile-config'
//import { convertHook } from '@flatfile/v2-shims'
import api from '@flatfile/api'
import { ParticipantsSummaryContainer } from 'components'
import flatfileDatadogTracker from 'flatfile-datadog-tracking'
//import { recordHook } from '@flatfile/plugin-record-hook'
import { isEmpty, noop, has } from 'lodash'
import PropTypes from 'prop-types'
import exact from 'prop-types-exact'
import { selectors as globalSelectors } from '../../js/main/reducer'
import { selectors as bdSelectors } from '../../js/main/benefit-distributions/reducer'
import { listeners } from '../validationsAndFormatting/listeners'
import { compose } from 'redux'
import { connect } from 'react-redux'
import * as Types from 'types'

const propTypes = {
  recordType: PropTypes.oneOf(Object.values(RecordTypes)).isRequired,
  constraints: PropTypes.object,
  buttonContent: PropTypes.node.isRequired,
  fileName: PropTypes.string,
  setFileName: PropTypes.func,
  //handleRecords: PropTypes.func,
  handleRecordsSubmit: PropTypes.func,
  handleValidData: PropTypes.func.isRequired,
  handleInvalidData: PropTypes.func.isRequired,
  currentUser: Types.user.isRequired,
  participantsForSubmission: PropTypes.arrayOf(Types.rolloverParticipant),
  selectedPaymentMethod: PropTypes.string,
}

const defaultProps = {
  //handleRecords: noop,
  handleRecordsSubmit: noop,
  fileName: null,
  setFileName: noop,
  participantsForSubmission: [],
}

/**
 * This function finds missing fields from the list of records using the list of required field keys
 *
 * @param {object} record
 * @param {string[]} fields
 * @returns {string[]} a list missing fields
 */
function findMissingFields(record, fields) {
  return fields.filter((field) => !has(record, field))
}

/**
 * Given an array of records and an array of fields, assign each field with an empty string to each record:
 * @param {object[]} records
 * @param {string[]} fields
 * @returns {*}
 */
function assignFields(records, fields) {
  return fields.reduce((accumulatedRecords, field) => {
    return accumulatedRecords.map((record) => ({
      ...record,
      [field]: '',
    }))
  }, records)
}

const FlatfileUpload = ({
  recordType,
  constraints,
  buttonContent,
  handleRecordsSubmit,
  //handleRecords,
  fileName,
  setFileName,
  handleValidData,
  handleInvalidData,
  currentUser,
  participantsForSubmission,
  selectedPaymentMethod,
}) => {
  const { open, openPortal, closePortal } = useFlatfile()
  const isUploadComplete = useRef(false)
  const uploadedFileName = useRef('')
  const [fileId, setFileId] = useState('')
  const userAudience = currentUser.accountAudience
  const numberOfParticipants = participantsForSubmission?.length

  const toggleFlatfileUpload = () => {
    if (!open) {
      flatfileDatadogTracker.startPortalTracking()
      openPortal()
    } else {
      closePortal()
    }
  }

  const flatfileConfig = {
    ...getFlatfileSchema(
      recordType,
      constraints,
      userAudience,
      currentUser.accountName
    ),
    actions: [
      {
        operation: 'submitAction',
        mode: 'foreground',
        label: 'Continue',
        type: 'string',
        description: 'Submit data',
        primary: true,
        constraints: [{ type: 'hasData' }],
      },
    ],
  }

  const fieldKeys = flatfileConfig.sheets[0].fields.map((field) => field.key)

  useListener((listener) => {
    listener.on(
      'job:ready',
      {
        job: 'workbook:submitAction',
      },
      async (event) => {
        const { jobId, workbookId, spaceId } = event.context
        // Start tracking submission
        flatfileDatadogTracker.startSubmissionTracking()

        const { data: workbookSheets } = await api.sheets.list({ workbookId })
        const fileName = (await api.files.get(fileId)).data.name
        uploadedFileName.current = fileName

        try {
          workbookSheets.forEach(async (sheet) => {
            const pageSize = 10000
            let allRecords = []
            const totalRecords = (await api.sheets.getRecordCounts(sheet.id))
              .data.counts.total
            const totalPages = Math.ceil(totalRecords / pageSize)
            for (let pageNumber = 1; pageNumber <= totalPages; pageNumber++) {
              const res = await api.records.get(sheet.id, {
                pageNumber,
                pageSize,
              })
              const records =
                res?.data.records ?? res?.data?.data?.records ?? []
              allRecords = allRecords.concat(records)
            }

            await api.jobs.ack(jobId, {
              info: 'Starting job to process the submitted record',
              progress: 10,
            })
            const { validData, invalidData } = allRecords.reduce(
              (acc, { values, valid }, index) => {
                const transformedValues = Object.fromEntries(
                  Object.entries(values).map(([key, val]) => [
                    key,
                    val.value ?? '',
                  ])
                )
                const record = { ...transformedValues, row: ++index }
                if (valid) acc.validData.push(record)
                else acc.invalidData.push(record)
                return acc
              },
              { validData: [], invalidData: [] }
            )
            setFileName(uploadedFileName.current)

            const missingFields = findMissingFields(validData[0], fieldKeys)
            const validDataWithAllFields = assignFields(
              validData,
              missingFields
            )

            await api.jobs.ack(jobId, {
              info: 'Job is completed',
              progress: 100,
            })

            const validDataExists = !isEmpty(validData)
            const invalidDataExists = !isEmpty(invalidData)

            if (validDataExists && !invalidDataExists) {
              isUploadComplete.current = validDataExists
              await api.jobs.complete(jobId, {
                outcome: {
                  acknowledge: true,
                  message: `Success! your ${RecordNames[
                    recordType
                  ].toLowerCase()} information has been captured`,
                },
              })
              handleValidData(validDataWithAllFields)
              // Track successful submission
              flatfileDatadogTracker.completeSubmissionTracking('success', {
                recordCount: validDataWithAllFields.length,
                validRecordCount: validDataWithAllFields.length,
                invalidRecordCount: 0,
                recordType,
              })
              handleRecordsSubmit && handleRecordsSubmit()
              closePortal()
            } else if (invalidDataExists && validDataExists) {
              handleInvalidData(invalidData)
              await api.jobs.complete(jobId, {
                outcome: {
                  acknowledge: true,
                  hideDefaultButton: true,
                  next: {
                    type: 'id',
                    id: spaceId,
                    path: `workbook/${workbookId}/sheet/${sheet.id}`,
                    query: 'filter=error',
                    label: 'Review invalid rows',
                  },
                  heading:
                    'You have one or more rows with unresolved formatting issues.',
                  message: `Click on the Go to invalid records button and fix the invalid record before submitting again.\n You can delete the invalid records.`,
                },
              })
              // Track partial submission
              flatfileDatadogTracker.completeSubmissionTracking('partial', {
                recordCount: validDataWithAllFields.length + invalidData.length,
                validRecordCount: validDataWithAllFields.length,
                invalidRecordCount: invalidData.length,
                recordType,
              })
            } else {
              await api.jobs.fail(jobId, {
                outcome: {
                  message: `No valid records were submitted. Please try again`,
                },
              })

              // Track failed submission (no valid records)
              flatfileDatadogTracker.completeSubmissionTracking('failure', {
                recordCount: allRecords.length,
                validRecordCount: 0,
                invalidRecordCount: allRecords.length,
                recordType,
              })
            }
          })
        } catch (error) {
          flatfileDatadogTracker.trackSubmissionError('api_error')
          await api.jobs.fail(jobId, {
            outcome: {
              message: `No valid records were submitted. Please try again. ${error}`,
            },
          })
          return
        }
      }
    )

    listener.on('upload:completed', async ({ context: { fileId } }) => {
      setFileId(fileId)
      flatfileDatadogTracker.trackFileUpload({ recordType })
    })

    // listener.use(
    //   recordHook(flatfileConfig.sheets[0].slug, (record) => {
    //     return convertHook(handleRecords)(record)
    //   })
    // )
  })

  // Apply specific module listeners

  if (recordType === RecordTypes.SEARCH) {
    useListener((listener) => listeners[recordType](listener))
  } else if (recordType === RecordTypes.BD_PARTICIPANT) {
    useListener((listener) =>
      listeners[recordType](listener, selectedPaymentMethod, userAudience)
    )
  } else {
    useListener((listener) => listeners[recordType](listener, userAudience))
  }

  return (
    <>
      {fileName || numberOfParticipants ? (
        <ParticipantsSummaryContainer
          numberOfParticipants={numberOfParticipants}
          handleUpload={() => {
            toggleFlatfileUpload()
          }}
        />
      ) : (
        <button
          className="button-primary-outline upload-file is-fullwidth"
          onClick={toggleFlatfileUpload}
        >
          {buttonContent}
        </button>
      )}

      <Space
        config={{
          name: `${RecordNames[recordType]} Record`,
          metadata: {
            sidebarConfig: {
              showSidebar: false,
            },
          },
        }}
      >
        <Document
          config={{
            title: 'Upload Instructions',
            body: UploadInstructions[recordType],
          }}
        />
        <Workbook config={flatfileConfig} />
      </Space>
    </>
  )
}

function mapStateToProps(state) {
  return {
    currentUser: globalSelectors.currentUser(state),
    selectedPaymentMethod: bdSelectors.selectedPaymentMethod(state),
  }
}

const mapDispatchToProps = {}

FlatfileUpload.propTypes = exact(propTypes)
FlatfileUpload.defaultProps = defaultProps

export default compose(connect(mapStateToProps, mapDispatchToProps))(
  React.memo(FlatfileUpload)
)
